import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, BookOpen, Settings, LogOut, ChevronLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";

const items = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testId: "admin-nav-overview", exact: true },
  { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users" },
  { to: "/admin/terms", label: "Terms", icon: BookOpen, testId: "admin-nav-terms" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--inmind-bg)" }} data-testid="admin-layout">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <header className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
                Admin · {user?.email}
              </div>
              <h1 className="font-serif text-3xl text-[var(--inmind-text)] leading-none mt-1">InThink Console</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-back-to-app"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/10 bg-white/70 text-[var(--inmind-text-secondary)] hover:bg-white transition-colors text-xs font-medium"
            >
              <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.7} /> Back to app
            </Link>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-black/10 bg-white/70 text-[var(--inmind-text-secondary)] hover:bg-white transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.7} />
            </button>
          </div>
        </header>

        <nav className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="admin-nav">
          {items.map(({ to, label, icon: Icon, testId, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={testId}
              className={({ isActive }) =>
                `whitespace-nowrap inline-flex items-center gap-1.5 text-xs tracking-wide uppercase font-semibold rounded-full px-3.5 py-2 transition-colors duration-200 ${
                  isActive
                    ? "bg-[var(--inmind-primary)] text-white"
                    : "bg-white text-[var(--inmind-text-secondary)] border border-black/10 hover:bg-black/5"
                }`
              }
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.7} /> {label}
            </NavLink>
          ))}
        </nav>

        <Outlet />
      </div>
    </div>
  );
}
